import { useAuth } from "@dl/web-core-js/auth";
import { useMemo } from "react";
import * as C from "./clients";

export * from "./types";
export * from "./clients";
export { PublicClient } from "./public";

/**
 * Every logic client, built once per session.
 *
 * A page asks for what it needs (`const { artworks, stores } = useApi()`)
 * rather than constructing clients itself, which keeps the `http` dependency
 * in one place and the memo key honest — clients are stateless wrappers, so
 * rebuilding them on every render would only churn query keys.
 */
export function useApi() {
  const { http } = useAuth();

  return useMemo(
    () => ({
      artworks: new C.ArtworkClient(http),
      artworkCores: new C.ArtworkCoreClient(http),
      artworkSearch: new C.ArtworkSearchClient(http),
      lazyArtworks: new C.LazyArtworkClient(http),
      artworkImages: new C.ArtworkImageClient(http),

      catalogs: new C.CatalogClient(http),
      collections: new C.CollectionClient(http),
      displayCollections: new C.DisplayCollectionClient(http),
      publishers: new C.PublisherClient(http),
      metadataTemplates: new C.PublisherMetadataTemplateClient(http),
      metadataFieldMappings: new C.PublisherMetadataTemplateFieldMappingClient(http),
      countries: new C.CountryClient(http),
      regions: new C.RegionClient(http),
      stores: new C.StoreClient(http),
      clients: new C.ClientClient(http),
      clientAddresses: new C.ClientMailingAddressClient(http),
      clientNotes: new C.ClientNoteClient(http),
      globalDpi: new C.GlobalDpiClient(http),

      products: new C.ProductClient(http),
      productCategories: new C.ProductCategoryClient(http),
      productEntities: new C.ProductEntityClient(http),
      productEntitiesWithImages: new C.ProductEntityWithImagesClient(http),
      storePriceLines: new C.ProductStorePriceLineClient(http),
      clientPriceLines: new C.ProductClientPriceLineClient(http),

      decors: new C.DecorClient(http),
      joliscreen: new C.JoliscreenClient(http),

      kpiClients: new C.KpiClientClient(http),
      kpiStores: new C.KpiStoreClient(http),

      orders: new C.OrderClient(http),
      draftOrders: new C.DraftOrderClient(http),
      orderNotes: new C.OrderNoteClient(http),
      shopifyOrders: new C.ShopifyOrderClient(http),
      shopifyProducts: new C.ShopifyProductClient(http),
      shopifyCollections: new C.ShopifyCollectionClient(http),

      productionCenters: new C.ProductionCenterClient(http),
      productionJobs: new C.ProductionJobClient(http),
      productionJobCores: new C.ProductionJobCoreClient(http),
      productionJobLines: new C.ProductionJobLineClient(http),
      legacyProductionJobs: new C.LegacyProductionJobClient(http),
      skuMatches: new C.SkuMatchClient(http),
      printSettings: new C.PrintSettingsClient(http),
      printSettingsLines: new C.PrintSettingsLineClient(http),
    }),
    [http],
  );
}

export type LogicApi = ReturnType<typeof useApi>;
